import { atom } from "jotai";
import { DCDesign, DCDesignColor } from "../design_creator/types";

// A design as returned by the designer-catalog endpoints — decorated with the
// owning brand's name and (on browse) whether it is already in the catalog.
// Colour-match metadata, present on every design only while a colour filter is
// active. delta_e is the raw CIEDE2000 distance; match_pct is the same value
// mapped onto the closeness scale for display.
export interface ColorMatchMeta {
    match_pct: number;
    delta_e: number;
    hex: string | null;
}

export type CatalogDesign = DCDesign & { brand_name?: string | null; color_match?: ColorMatchMeta };
export type BrowsableDesign = CatalogDesign & { in_catalog: boolean };

export interface CatalogResponse<T> {
    designs: T[];
    // Server-driven: the API fetches limit + 1 rows and reports whether the
    // extra one existed. Never infer this from `designs.length < total` — that
    // is what stalled the catalog at ~1000 designs, because `total` came from a
    // query PostgREST had already truncated.
    hasNextPage: boolean;
    // Composite cursor (brand_id, sortValue, id) of the last row on this page.
    // Echo it back as `after` to get the next page.
    endCursor: string | null;
    // Exact overall + per-brand totals for the current filters (brand name ->
    // count). Returned on the first page only; the UI keeps them across
    // load-more since the filters are fixed for the duration of a scroll.
    total?: number;
    brandCounts?: Record<string, number>;
}

// Optional list filters/sort sent to the browse + catalog endpoints.
export interface CatalogFilters {
    patternTypes?: string[];
    brandIds?: number[];
    sort?: 'newest' | 'title';
    // "Shop by colour": a '#RRGGBB' base colour plus how close a design has to be.
    // When colorHex is set the backend returns a flat list sorted by colour
    // distance, so brand grouping and the sort control are bypassed.
    colorHex?: string;
    colorCloseness?: number;
}

// A brand offered in the catalog's brand filter.
export interface CatalogBrand {
    id: number;
    name: string;
}

// Pattern-type options for the catalog filter (values match dc_designs.pattern_type,
// mirroring the design library's pattern-type filter).
export const CATALOG_PATTERN_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: 'Oversized Repeat', label: 'Oversized Repeat' },
    { value: 'Infinity Mural', label: 'Infinity Mural' },
    { value: 'Mural', label: 'Mural' },
    { value: 'Half-Drop Repeat', label: 'Half-Drop Repeat' },
    { value: 'Half-Brick Repeat', label: 'Half-Brick Repeat' },
];

// Default closeness for the colour slider. 50% ~= deltaE 25, which is loose
// enough to return a usable palette without matching everything.
export const DEFAULT_COLOR_CLOSENESS = 50;

// Preset swatches for the colour picker. Designers shop by colour name far more
// often than by hex, so the presets are the primary control and the custom hex
// input is the escape hatch.
export const CATALOG_COLOR_PRESETS: { name: string; hex: string }[] = [
    { name: 'White', hex: '#FFFFFF' },
    { name: 'Cream', hex: '#F5EFE0' },
    { name: 'Beige', hex: '#E3D5C0' },
    { name: 'Tan', hex: '#C9A87C' },
    { name: 'Brown', hex: '#7A5230' },
    { name: 'Terracotta', hex: '#C56643' },
    { name: 'Black', hex: '#111111' },
    { name: 'Charcoal', hex: '#3A3A3A' },
    { name: 'Grey', hex: '#9AA0A6' },
    { name: 'Navy', hex: '#1B2A4A' },
    { name: 'Blue', hex: '#2F6FB8' },
    { name: 'Sky', hex: '#A8CFE8' },
    { name: 'Teal', hex: '#1F7A72' },
    { name: 'Green', hex: '#2F7A44' },
    { name: 'Sage', hex: '#A3B18A' },
    { name: 'Olive', hex: '#6B7238' },
    { name: 'Yellow', hex: '#E9C64B' },
    { name: 'Gold', hex: '#C9A227' },
    { name: 'Orange', hex: '#E2762F' },
    { name: 'Red', hex: '#B3312C' },
    { name: 'Pink', hex: '#E29CB2' },
    { name: 'Blush', hex: '#F0D3D1' },
    { name: 'Purple', hex: '#6B4A8F' },
    { name: 'Plum', hex: '#5B2A45' },
];

export interface DesignDetails {
    tags: string[];
    colors: DCDesignColor[];
    /** Repeat size (inches) from the design's default scale option, when available. */
    repeat_width_inches?: number | null;
    repeat_height_inches?: number | null;
}

// Browse tab state.
// `hasMore` now mirrors the server's `hasNextPage` verbatim instead of being
// derived from a (previously truncated) total.
export const browseDesignsAtom = atom<BrowsableDesign[]>([]);
export const browseTotalAtom = atom<number>(0);
export const browseHasMoreAtom = atom<boolean>(true);
export const browseLoadingAtom = atom<boolean>(false);
export const browsePaginationLoadingAtom = atom<boolean>(false);
// Last `endCursor` per tab, mirrored from the hook's refs for anything that
// needs to read it. The refs are the source of truth for the next request.
export const browseCursorAtom = atom<string | null>(null);

// My Catalog tab state
export const catalogDesignsAtom = atom<CatalogDesign[]>([]);
export const catalogTotalAtom = atom<number>(0);
export const catalogHasMoreAtom = atom<boolean>(true);
export const catalogLoadingAtom = atom<boolean>(false);
export const catalogPaginationLoadingAtom = atom<boolean>(false);
export const catalogCursorAtom = atom<string | null>(null);

// Per-brand totals (brand name -> count) for the current filters, per tab.
export const browseBrandCountsAtom = atom<Record<string, number>>({});
export const catalogBrandCountsAtom = atom<Record<string, number>>({});

// Shared UI state
export const designerCatalogSearchAtom = atom<string>('');
export const designerCatalogTabAtom = atom<number>(0);

// Colour filter state, shared so the catalog page and the recommended-designs
// picker can both drive "shop by colour".
export const catalogColorHexAtom = atom<string | undefined>(undefined);
export const catalogColorClosenessAtom = atom<number>(DEFAULT_COLOR_CLOSENESS);
